import FetchOptionChain from "../Apis/FetchOptionChain";
import FetchOptionChainPrices from "../Apis/FetchOptionChainPrices";
import { resetAllDashboards } from "./selectionActions";

export const setOptionsData = (data, portfolioId, detailsHash) => ({
  type: "SET_OPTIONS_DATA",
  payload: { data, portfolioId, detailsHash },
});

export const setLoading = (isLoading) => ({
  type: "SET_LOADING",
  payload: isLoading,
});

export const setError = (error) => ({
  type: "SET_ERROR",
  payload: error,
});

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const chunkArray = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const getDynamicTickerChunkSize = (tickerCount) => {
  if (tickerCount <= 5) return 1;
  if (tickerCount <= 10) return 2;
  if (tickerCount <= 25) return 3;
  if (tickerCount <= 40) return 4;
  return 5;
};

const mergePricedData = (targetData, sourceData) => {
  ["C", "P"].forEach((type) => {
    Object.keys(sourceData?.[type] || {}).forEach((stock) => {
      if (!targetData[type][stock]) {
        targetData[type][stock] = [];
      }
      targetData[type][stock].push(...sourceData[type][stock]);
    });
  });
};

const buildOptionDataChunk = (optionsData, tickers) =>
  tickers.reduce((acc, ticker) => {
    acc[ticker] = optionsData[ticker];
    return acc;
  }, {});

const fetchPriceChunkWithFallback = async ({
  optionsData,
  tickers,
  singleTickerRetryCount = 0,
}) => {
  try {
    const optionDataChunk = buildOptionDataChunk(optionsData, tickers);
    return await FetchOptionChainPrices(optionDataChunk);
  } catch (error) {
    if (tickers.length > 1) {
      const splitIndex = Math.ceil(tickers.length / 2);

      const [leftData, rightData] = await Promise.all([
        fetchPriceChunkWithFallback({
          optionsData,
          tickers: tickers.slice(0, splitIndex),
        }),
        fetchPriceChunkWithFallback({
          optionsData,
          tickers: tickers.slice(splitIndex),
        }),
      ]);

      const merged = { C: {}, P: {} };
      mergePricedData(merged, leftData);
      mergePricedData(merged, rightData);

      return merged;
    }

    if (singleTickerRetryCount < 2) {
      await wait(1500 * (singleTickerRetryCount + 1));
      return fetchPriceChunkWithFallback({
        optionsData,
        tickers,
        singleTickerRetryCount: singleTickerRetryCount + 1,
      });
    }

    throw error;
  }
};

const parseDate = (dateStr) => new Date(dateStr);

const findClosestOption = (options, targetDate, targetMultiplier) => {
  let bestOption = null;
  let bestScore = Infinity;
  for (const option of options) {
    const dateDiff = Math.abs(parseDate(option.ExpirationDate) - targetDate);
    const strikeDiff = Math.abs(
      option.Strike - option.ClosePrice * targetMultiplier,
    );
    const score = dateDiff + strikeDiff;
    if (score < bestScore) {
      bestScore = score;
      bestOption = option;
    }
  }
  return bestOption;
};

const processOptions = (optionsData, isCall) => {
  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  const targetDate = new Date(oneMonthFromNow - 1);
  const targetMultiplier = isCall ? 1.1 : 0.9;
  const result = {};
  Object.keys(optionsData).forEach((ticker) => {
    const options = optionsData[ticker];
    const closestOption = findClosestOption(
      options,
      targetDate,
      targetMultiplier,
    );
    if (closestOption) {
      result[ticker] = closestOption;
    }
  });
  return result;
};

const calculateTotalPremiumAndCost = (closestOptions, portfolioData) => {
  let totalPremium = 0;
  let totalCost = 0;
  const portfolioMap = Object.fromEntries(
    portfolioData.map((p) => [p.ticker, p]),
  );
  Object.entries(closestOptions.calls).forEach(([ticker, callOption]) => {
    const bidPrice = parseFloat(callOption.CloseBidPrice);
    const portfolioItem = portfolioMap[ticker];
    const quantityMultiplier = portfolioItem
      ? Math.floor(portfolioItem.quantity / 100)
      : 0;
    totalPremium += bidPrice * quantityMultiplier * 100;
  });
  sessionStorage.setItem("totalPremium", JSON.stringify(totalPremium));
  sessionStorage.setItem("totalCost", JSON.stringify(totalCost));
};

const fetchAllOptionPrices = async ({ portfolioData, dispatch }) => {
  const combinedPricedData = { C: {}, P: {} };

  const chainResults = await Promise.allSettled(
    portfolioData.map((item) => FetchOptionChain([item])),
  );

  const optionsData = {};
  chainResults.forEach((result) => {
    if (result.status === "fulfilled") {
      Object.assign(optionsData, result.value);
    } else {
      console.error("Error fetching option chain:", result.reason);
    }
  });

  const tickerList = Object.keys(optionsData);
  const tickerBatchSize = getDynamicTickerChunkSize(tickerList.length);
  const tickerBatches = chunkArray(tickerList, tickerBatchSize);
  const priceResults = await Promise.allSettled(
    tickerBatches.map((batch) =>
      fetchPriceChunkWithFallback({
        optionsData,
        tickers: batch,
      }),
    ),
  );

  priceResults.forEach((result) => {
    if (result.status === "rejected") {
      dispatch(setError(result.reason?.toString?.() || "Unknown error"));
      return;
    }
    mergePricedData(combinedPricedData, result.value);
  });

  return combinedPricedData;
};

export const fetchOptionsDataIfNeeded =
  (portfolioData, portfolioId) => async (dispatch, getState) => {
    const { options } = getState();
    if (options.isLoading) return;
    dispatch(resetAllDashboards());
    const detailsHash = JSON.stringify(portfolioData);
    const storedOptimizerPortfolioId = parseInt(
      sessionStorage.getItem("optimizerPortfolioId"),
    );
    if (storedOptimizerPortfolioId !== portfolioId) {
      sessionStorage.removeItem("optimizerData");
      sessionStorage.removeItem("optimizedPremium");
      sessionStorage.removeItem("optimizerPortfolioId");
    }
    sessionStorage.removeItem("callWritingCartItems");
    sessionStorage.removeItem("putBuyingCartItems");
    sessionStorage.removeItem("existingOptionHoldings");
    sessionStorage.removeItem("selectedRecommendations");
    dispatch(setLoading(true));
    try {
      const combinedPricedData = await fetchAllOptionPrices({
        portfolioData,
        dispatch,
      });
      const closestOptions = {
        calls: processOptions(combinedPricedData.C, true),
        puts: processOptions(combinedPricedData.P, false),
      };

      calculateTotalPremiumAndCost(closestOptions, portfolioData);

      dispatch(setOptionsData(combinedPricedData, portfolioId, detailsHash));
    } catch (error) {
      dispatch(setError(error.toString()));
    } finally {
      dispatch(resetAllDashboards());
      dispatch(setLoading(false));
    }
  };
